import { DayMode } from '../routine/enums';
import type { DailyStep } from '../routine/models';
import type { DailyStepId } from '../shared/ids';
import { AdaptationDecisionType, type AdaptationRuleId, UserChoice } from './enums';
import { computePriorityOrder, RULE_BY_MODE } from './PriorityRules';

/**
 * Adaptive Guidance System — Phase 1 cognitive cycle (adaptive-guidance/COGNITIVE_MODEL.md).
 * Five layers, strict dependency order (Observation -> Comprehension -> Evaluation -> Decision
 * -> Expression), each a separately testable pure function. Local, deterministic, no network
 * call, no generative model. The cycle never executes anything itself — Decision only ever
 * *proposes* a candidate order; applying it is the caller's responsibility, through the
 * existing reorder mechanism, and only after explicit user acceptance.
 */

// ---------------------------------------------------------------------------
// Observation — captures signals as given, without interpreting them.
// ---------------------------------------------------------------------------

export type SignalCategory = 'day_mode' | 'daily_steps';

export interface Signal<Value = unknown> {
  readonly category: SignalCategory;
  readonly value: Value;
  readonly source: 'routine_state';
  readonly capturedAt: string;
}

export function observe(
  dayMode: DayMode,
  steps: readonly DailyStep[],
  timestamp: string,
): readonly Signal[] {
  return [
    { category: 'day_mode', value: dayMode, source: 'routine_state', capturedAt: timestamp },
    { category: 'daily_steps', value: steps, source: 'routine_state', capturedAt: timestamp },
  ];
}

// ---------------------------------------------------------------------------
// Comprehension — assembles the observed signals into a factual description.
// No rule is applied here; no judgement is made.
// ---------------------------------------------------------------------------

export interface SituationDescription {
  readonly dayMode: DayMode;
  readonly steps: readonly DailyStep[];
  readonly currentOrder: readonly DailyStepId[];
  readonly describedAt: string;
}

/** Returns undefined when a required signal is missing — an Observation failure that invalidates the cycle. */
export function understand(signals: readonly Signal[]): SituationDescription | undefined {
  const modeSignal = signals.find((signal) => signal.category === 'day_mode');
  const stepsSignal = signals.find((signal) => signal.category === 'daily_steps');
  if (!modeSignal || !stepsSignal) return undefined;
  const steps = stepsSignal.value as readonly DailyStep[];
  return {
    dayMode: modeSignal.value as DayMode,
    steps,
    currentOrder: steps.map((step) => step.id),
    describedAt: stepsSignal.capturedAt,
  };
}

// ---------------------------------------------------------------------------
// Evaluation — applies the authorized rule for the day mode and identifies
// candidate options. Never chooses the final action itself.
// ---------------------------------------------------------------------------

export interface AdaptationOption {
  readonly ruleId: AdaptationRuleId;
  readonly proposedOrder: readonly DailyStepId[];
  readonly affectedStepIds: readonly DailyStepId[];
}

/** Zero or one candidate for Phase 1. A candidate is only produced if it changes something real. */
export function evaluate(situation: SituationDescription): readonly AdaptationOption[] {
  const ruleId = RULE_BY_MODE[situation.dayMode];
  if (!ruleId) return [];
  const proposedOrder = computePriorityOrder(situation.steps, situation.dayMode, ruleId);
  const affectedStepIds = proposedOrder.filter((id, index) => id !== situation.currentOrder[index]);
  if (affectedStepIds.length === 0) return [];
  return [{ ruleId, proposedOrder, affectedStepIds }];
}

// ---------------------------------------------------------------------------
// Decision — resolves the options against the preseance table. Never executes
// anything; "do nothing" is always a valid outcome.
// ---------------------------------------------------------------------------

export interface AdaptationDecision {
  readonly type: AdaptationDecisionType;
  readonly option?: AdaptationOption;
  readonly reasonCode:
    | 'ags_disabled'
    | 'observation_failed'
    | 'no_change_needed'
    | 'prior_choice_accepted'
    | 'prior_choice_rejected'
    | 'proposed';
}

export interface DecisionInput {
  /** Preseance #1 — mise hors service (kill switch). Defaults to enabled; see closure doc. */
  readonly agsEnabled: boolean;
  /** Preseance #3 — the user's own explicit choice always outranks a fresh proposal. */
  readonly priorChoice?: UserChoice;
}

export function decide(
  options: readonly AdaptationOption[],
  input: DecisionInput,
): AdaptationDecision {
  if (!input.agsEnabled) return { type: AdaptationDecisionType.abstain, reasonCode: 'ags_disabled' };
  const option = options[0];
  if (!option) return { type: AdaptationDecisionType.abstain, reasonCode: 'no_change_needed' };
  if (input.priorChoice) {
    return {
      type: AdaptationDecisionType.respectPriorChoice,
      option,
      reasonCode: input.priorChoice === UserChoice.accepted ? 'prior_choice_accepted' : 'prior_choice_rejected',
    };
  }
  return { type: AdaptationDecisionType.propose, option, reasonCode: 'proposed' };
}

// ---------------------------------------------------------------------------
// Expression — turns the decision into a structured message code + parameters.
// Never produces human-readable text directly; the presentation layer resolves
// the code against the i18n catalog.
// ---------------------------------------------------------------------------

export type GuidanceMessageCode =
  | 'abstain_default'
  | 'abstain_ags_disabled'
  | 'abstain_observation_failed'
  | 'respect_accepted'
  | 'respect_rejected'
  | 'propose';

export interface GuidanceExplanation {
  readonly code: GuidanceMessageCode;
  readonly modeKey: string;
  readonly ruleId?: AdaptationRuleId;
  readonly affectedCount: number;
}

/** Maps DayMode enum values to i18n catalog mode key suffixes (mode.*). */
const MODE_CATALOG_KEY: Readonly<Record<DayMode, string>> = {
  [DayMode.normal]: 'normal',
  [DayMode.busy]: 'charge',
  [DayMode.travel]: 'voyage',
  [DayMode.recovery]: 'recuperation',
  [DayMode.difficult]: 'difficile',
  [DayMode.elevation]: 'elevation',
  [DayMode.momentDecisif]: 'moment_decisif',
  [DayMode.imprevu]: 'imprevu',
  [DayMode.transition]: 'transition',
};

export function express(decision: AdaptationDecision, dayMode: DayMode): GuidanceExplanation {
  const modeKey = MODE_CATALOG_KEY[dayMode];
  if (decision.type === AdaptationDecisionType.abstain) {
    const code: GuidanceMessageCode =
      decision.reasonCode === 'ags_disabled'
        ? 'abstain_ags_disabled'
        : decision.reasonCode === 'observation_failed'
          ? 'abstain_observation_failed'
          : 'abstain_default';
    return { code, modeKey, affectedCount: 0 };
  }
  const option = decision.option;
  const affectedCount = option?.affectedStepIds.length ?? 0;
  const ruleId = option?.ruleId;
  if (decision.type === AdaptationDecisionType.respectPriorChoice) {
    const code: GuidanceMessageCode =
      decision.reasonCode === 'prior_choice_accepted' ? 'respect_accepted' : 'respect_rejected';
    if (ruleId) return { code, modeKey, ruleId, affectedCount };
    return { code, modeKey, affectedCount };
  }
  if (ruleId) return { code: 'propose', modeKey, ruleId, affectedCount };
  return { code: 'propose', modeKey, affectedCount };
}

// ---------------------------------------------------------------------------
// Trace — minimal, append-only record sufficient for explicability.
// ---------------------------------------------------------------------------

export interface AdaptationTraceEvent {
  readonly logicalDate: string;
  readonly dayMode: DayMode;
  readonly ruleId?: AdaptationRuleId;
  readonly decisionType: AdaptationDecisionType;
  readonly reasonCode: AdaptationDecision['reasonCode'];
  readonly recordedAt: string;
}

export function buildTraceEvent(
  logicalDate: string,
  decision: AdaptationDecision,
  situation: SituationDescription | undefined,
  timestamp: string,
): AdaptationTraceEvent {
  return {
    logicalDate,
    dayMode: situation?.dayMode ?? DayMode.normal,
    ...(decision.option ? { ruleId: decision.option.ruleId } : {}),
    decisionType: decision.type,
    reasonCode: decision.reasonCode,
    recordedAt: timestamp,
  };
}

// ---------------------------------------------------------------------------
// Orchestrator — runs the five layers in their mandatory dependency order.
// ---------------------------------------------------------------------------

export interface AdaptationCycleResult {
  readonly decision: AdaptationDecision;
  readonly explanation: GuidanceExplanation;
  readonly situation?: SituationDescription;
}

export function runAdaptationCycle(input: {
  readonly dayMode: DayMode;
  readonly steps: readonly DailyStep[];
  readonly timestamp: string;
  readonly agsEnabled: boolean;
  readonly priorChoice?: UserChoice;
}): AdaptationCycleResult {
  const signals = observe(input.dayMode, input.steps, input.timestamp);
  const situation = understand(signals);
  if (!situation) {
    const decision: AdaptationDecision = {
      type: AdaptationDecisionType.abstain,
      reasonCode: 'observation_failed',
    };
    return { decision, explanation: express(decision, input.dayMode) };
  }
  const options = evaluate(situation);
  const decision = decide(options, {
    agsEnabled: input.agsEnabled,
    ...(input.priorChoice ? { priorChoice: input.priorChoice } : {}),
  });
  const explanation = express(decision, situation.dayMode);
  return { decision, explanation, situation };
}
